/*
 * Purpose: Onboarding Wizard — detect first-run steps and platform readiness
 * Last modified: 2026-06-25 | Agent
 * Completeness: 92
 */
import { hasResolvedSecret } from '@/lib/resolveSecret';
import { useSecretStore } from '@/stores/secretStore';
import { hasGoogleOAuthConfig, getGmailAccessToken } from '@/services/connectors/gmailOAuth';
import { hasLinkedInOAuthConfig, isLinkedInConnected } from '@/services/connectors/linkedinOAuth';

export type SetupStepId =
  | 'welcome'
  | 'vault'
  | 'secrets'
  | 'google'
  | 'gmail'
  | 'linkedin'
  | 'done';

export interface SetupStepDef {
  id: SetupStepId;
  label: string;
  shortLabel: string;
  optional?: boolean;
}

export const SETUP_STEPS: SetupStepDef[] = [
  { id: 'welcome', label: 'Welcome', shortLabel: 'Start' },
  { id: 'vault', label: 'Secrets vault', shortLabel: 'Vault' },
  { id: 'secrets', label: 'API keys', shortLabel: 'Keys' },
  { id: 'google', label: 'Google Cloud', shortLabel: 'Google' },
  { id: 'gmail', label: 'Connect Gmail', shortLabel: 'Gmail' },
  { id: 'linkedin', label: 'LinkedIn', shortLabel: 'LinkedIn', optional: true },
  { id: 'done', label: 'All set', shortLabel: 'Done' },
];

const SETUP_COMPLETE_KEY = 'autoblocks.setup.completedAt';

export function readSetupSnapshot() {
  const store = useSecretStore.getState();
  return {
    hasEncryptedVault: store.hasEncryptedVault,
    isUnlocked: store.isUnlocked,
    hasOpenAi: hasResolvedSecret('OPENAI_API_KEY'),
    hasGoogleClientId: hasGoogleOAuthConfig(),
    gmailConnected: Boolean(getGmailAccessToken()),
    linkedInConfigured: hasLinkedInOAuthConfig(),
    linkedInConnected: isLinkedInConnected(),
  };
}

export function isStepComplete(id: SetupStepId, snap = readSetupSnapshot()): boolean {
  switch (id) {
    case 'welcome':
      return true;
    case 'vault':
      return snap.isUnlocked;
    case 'secrets':
      return snap.hasOpenAi;
    case 'google':
      return snap.hasGoogleClientId;
    case 'gmail':
      return snap.gmailConnected;
    case 'linkedin':
      return snap.linkedInConnected || snap.linkedInConfigured;
    case 'done':
      return isCoreSetupComplete(snap);
    default:
      return false;
  }
}

/** Minimum to start Compose with Gmail job flows */
export function isCoreSetupComplete(snap = readSetupSnapshot()): boolean {
  return snap.isUnlocked && snap.hasGoogleClientId && snap.gmailConnected;
}

export function isRecommendedSetupComplete(snap = readSetupSnapshot()): boolean {
  return isCoreSetupComplete(snap) && snap.hasOpenAi;
}

export function getFirstActionableStep(snap = readSetupSnapshot()): SetupStepId {
  for (const step of SETUP_STEPS) {
    if (step.id === 'welcome' || step.id === 'done') continue;
    if (!isStepComplete(step.id, snap)) return step.id;
  }
  return 'done';
}

export function setupProgressPercent(snap = readSetupSnapshot()): number {
  const tracked = SETUP_STEPS.filter((s) => s.id !== 'welcome' && s.id !== 'done' && !s.optional);
  const done = tracked.filter((s) => isStepComplete(s.id, snap)).length;
  return Math.round((done / tracked.length) * 100);
}

export function markSetupCompleted(): void {
  localStorage.setItem(SETUP_COMPLETE_KEY, new Date().toISOString());
}

export function wasSetupMarkedComplete(): boolean {
  return Boolean(localStorage.getItem(SETUP_COMPLETE_KEY));
}

export function shouldShowSetupBanner(snap = readSetupSnapshot()): boolean {
  if (isCoreSetupComplete(snap)) return false;
  return true;
}
